import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { FertilizerForm } from './-fertilizer-form'
import type { Fertilizer } from '@/lib/api/fertilizers-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateFertilizer,
    useDeleteFertilizer,
    useFertilizers,
    useUpdateFertilizer,
} from '@/hooks/use-fertilizers-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/fertilizers',
)({
    component: FertilizersPage,
})

function FertilizersPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('fertilizers.write')
    const [editing, setEditing] = useState<Fertilizer | null>(null)
    const [open, setOpen] = useState(false)

    const { data } = useFertilizers({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreateFertilizer()
    const updateMut = useUpdateFertilizer(editing?.id ?? '')
    const deleteMut = useDeleteFertilizer()

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">
                    {m.fertilizer_title()}
                </h1>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.fertilizer_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead />
                        <TableHead>{m.fertilizer_name()}</TableHead>
                        <TableHead>{m.fertilizer_supplier()}</TableHead>
                        <TableHead>{m.fertilizer_unit_price()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell className="w-12">
                                {row.image_url && (
                                    <img
                                        src={row.image_url}
                                        alt={row.name}
                                        className="size-10 rounded object-cover"
                                    />
                                )}
                            </TableCell>
                            <TableCell className="font-medium">
                                {row.name}
                            </TableCell>
                            <TableCell>{row.supplier ?? '—'}</TableCell>
                            <TableCell>{row.unit_price ?? '—'}</TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.fertilizer_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.fertilizer_edit() : m.fertilizer_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <FertilizerForm
                        initial={editing}
                        onSubmit={async (values) => {
                            try {
                                if (editing) {
                                    await updateMut.mutateAsync(values)
                                } else {
                                    await createMut.mutateAsync(values)
                                }
                                setOpen(false)
                            } catch {
                                // onError handler in the mutation hook surfaces the toast
                            }
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}
